import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { MdOpenWith } from "react-icons/md";
import { supabase } from "../../supabaseClient";
import {
  generateCompositeImage,
  ALIGNMENT_OPTIONS,
  LABEL_FONT_FAMILIES,
  LABEL_FONT_WEIGHTS,
} from "../../utils/compositeImage";

const ALIGNMENT_LABELS = {
  "top-left": "Top Left",
  "top-right": "Top Right",
  center: "Center",
  "bottom-left": "Bottom Left",
  "bottom-right": "Bottom Right",
};

// The overlay ("highlight") image can be a Poster, Portrait, or Background —
// matching the three auto-generated dimensions. Other fields (banners,
// logos) don't make sense as a badge composited over a background image.
const OVERLAY_IMAGE_FIELDS = ["poster", "portrait", "background"];

export const safeJsonParseArray = (value) => {
  if (
    typeof value === "string" &&
    value.trim().startsWith("[") &&
    value.trim().endsWith("]")
  ) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [value];
    } catch {
      return [value];
    }
  }
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

export const resolveHighlightUrl = (path, base) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  // Composite images are uploaded under "row-assets/highlight/..." (already a
  // full relative key) — only bare tile-relative paths need "tiles/" added.
  if (path.startsWith("tiles/") || path.startsWith("row-assets/")) return `${base}${path}`;
  return `${base}tiles/${path}`;
};

export const getOverlayImageFields = (tile) =>
  OVERLAY_IMAGE_FIELDS.filter((field) => safeJsonParseArray(tile[field]).length > 0);

export const buildSlotKey = (baseTid, baseImage) => `${baseTid}::${baseImage}`;

// Debounced text input for the badge label — commits the value 500ms after
// the admin stops typing, instead of regenerating the composite (a full
// canvas redraw + toBlob) on every keystroke.
export const LabelTextInput = ({ value, onCommit }) => {
  const [text, setText] = useState(value || "");

  useEffect(() => {
    setText(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (text !== (value || "")) onCommit(text);
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="Badge text (e.g. NEW RELEASE)"
      className="w-full text-xs py-1 px-1 border rounded border-gray-300"
    />
  );
};

// Segmented Solid/Gradient toggle, shared by the text-color and
// background-color pickers.
export const ColorModeToggle = ({ mode, onChange }) => (
  <div className="flex rounded overflow-hidden border border-gray-300 text-[10px] shrink-0">
    {["solid", "gradient"].map((m) => (
      <button
        key={m}
        type="button"
        onClick={() => onChange(m)}
        className={`px-2 py-0.5 ${
          mode === m ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
        }`}
      >
        {m === "solid" ? "Solid" : "Gradient"}
      </button>
    ))}
  </div>
);

// One solid color swatch, or two swatches ("from" → "to") when in gradient
// mode — used for both the text-fill and background-pill color pickers.
export const ColorPicker = ({ mode, solid, from, to, onSolidChange, onFromChange, onToChange }) =>
  mode === "gradient" ? (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        className="w-6 h-6 p-0 border border-gray-300 rounded"
        title="Gradient start"
      />
      <span className="text-[10px] text-gray-400">→</span>
      <input
        type="color"
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        className="w-6 h-6 p-0 border border-gray-300 rounded"
        title="Gradient end"
      />
    </div>
  ) : (
    <input
      type="color"
      value={solid}
      onChange={(e) => onSolidChange(e.target.value)}
      className="w-6 h-6 p-0 border border-gray-300 rounded"
    />
  );

// A drag handle overlaid on the slot's preview thumbnail, letting the admin
// drop the badge anywhere on the image rather than only at 5 fixed
// corner/center slots. It's a plain placeholder box (not styled to match the
// real font/colors) — the actual styling only appears in the canvas-rendered
// preview once the drag commits and a fresh composite is generated.
export const DraggableLabel = ({ slot, containerRef, onCommit }) => {
  const [dragPos, setDragPos] = useState(null);
  const draggingRef = useRef(false);
  const lastPosRef = useRef(null);

  if (!slot.labelText || !slot.labelText.trim()) return null;

  const xPct = dragPos ? dragPos.xPct : slot.labelXPct ?? 0.05;
  const yPct = dragPos ? dragPos.yPct : slot.labelYPct ?? 0.05;

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    const startXPct = xPct;
    const startYPct = yPct;
    draggingRef.current = true;
    lastPosRef.current = null;

    const handleMove = (moveEvent) => {
      if (!draggingRef.current) return;
      const dx = (moveEvent.clientX - startClientX) / rect.width;
      const dy = (moveEvent.clientY - startClientY) / rect.height;
      const nextX = Math.min(Math.max(startXPct + dx, 0), 0.92);
      const nextY = Math.min(Math.max(startYPct + dy, 0), 0.92);
      lastPosRef.current = { xPct: nextX, yPct: nextY };
      setDragPos(lastPosRef.current);
    };

    const handleUp = () => {
      draggingRef.current = false;
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      // Commit as a plain statement here — NOT from inside a setState
      // updater. Calling onCommit (which cascades into the parent's
      // regenerateSlot -> setSlots) from within setDragPos's updater
      // function makes that updater impure, which is exactly what trips
      // React's "Maximum update depth exceeded" safeguard.
      if (lastPosRef.current) {
        onCommit(lastPosRef.current.xPct, lastPosRef.current.yPct);
      }
      setDragPos(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  // Invisible by default (the real preview <img> beneath already shows the
  // actual baked-in badge, so permanently rendering a duplicate box/outline
  // here was just visual clutter) — a grab icon appears directly over the
  // real text only on hover, hinting it's draggable, and the text box
  // reappears for feedback while actively dragging, then disappears again
  // once dropped.
  const isDragging = Boolean(dragPos);

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`group absolute z-10 flex min-w-[20px] min-h-[16px] select-none items-center justify-center whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold ${
        isDragging
          ? "cursor-grabbing border border-dashed border-white/80 bg-black/50 text-white"
          : "cursor-grab bg-transparent text-transparent"
      }`}
      style={{ left: `${xPct * 100}%`, top: `${yPct * 100}%` }}
      title="Drag to reposition"
    >
      {isDragging ? (
        slot.labelText
      ) : (
        <MdOpenWith className="opacity-0 group-hover:opacity-100 text-white drop-shadow" size={16} />
      )}
    </div>
  );
};

const OVERLAY_MIN_SIZE_PCT = 0.08;

// A freely-draggable AND resizable box overlaid on the slot's preview
// thumbnail — used only for plain backgrounds with no designed cutout, so
// the admin can position and size the overlay image anywhere instead of only
// choosing a fixed corner/center. Same invisible-until-hover treatment as
// DraggableLabel, plus a small resize handle at the bottom-right corner.
export const DraggableOverlay = ({ slot, containerRef, onCommit }) => {
  const [dragBox, setDragBox] = useState(null);
  const modeRef = useRef(null); // "move" | "resize" | null
  const lastBoxRef = useRef(null);

  if (!slot.overlayTid) return null;

  const box = dragBox || {
    xPct: slot.overlayXPct ?? 0.35,
    yPct: slot.overlayYPct ?? 0.35,
    widthPct: slot.overlayWidthPct ?? 0.3,
    heightPct: slot.overlayHeightPct ?? 0.3,
  };

  const startInteraction = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startClientX = e.clientX;
    const startClientY = e.clientY;
    const start = { ...box };
    modeRef.current = mode;
    lastBoxRef.current = null;

    const handleMove = (moveEvent) => {
      if (!modeRef.current) return;
      const dx = (moveEvent.clientX - startClientX) / rect.width;
      const dy = (moveEvent.clientY - startClientY) / rect.height;
      let next;
      if (modeRef.current === "move") {
        next = {
          ...start,
          xPct: Math.min(Math.max(start.xPct + dx, 0), 1 - start.widthPct),
          yPct: Math.min(Math.max(start.yPct + dy, 0), 1 - start.heightPct),
        };
      } else {
        next = {
          ...start,
          widthPct: Math.min(Math.max(start.widthPct + dx, OVERLAY_MIN_SIZE_PCT), 1 - start.xPct),
          heightPct: Math.min(Math.max(start.heightPct + dy, OVERLAY_MIN_SIZE_PCT), 1 - start.yPct),
        };
      }
      lastBoxRef.current = next;
      setDragBox(next);
    };

    const handleUp = () => {
      modeRef.current = null;
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      // Plain statement, not inside setDragBox's updater — same reason as
      // DraggableLabel's handleUp: a side effect (onCommit cascading into
      // regenerateSlot -> setSlots) inside a setState updater makes that
      // updater impure, which is what trips React's "Maximum update depth
      // exceeded" safeguard.
      if (lastBoxRef.current) {
        onCommit(lastBoxRef.current);
      }
      setDragBox(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const isActive = Boolean(dragBox);

  return (
    <div
      className={`group absolute z-10 border ${
        isActive ? "border-dashed border-white/80 bg-white/10" : "border-transparent"
      }`}
      style={{
        left: `${box.xPct * 100}%`,
        top: `${box.yPct * 100}%`,
        width: `${box.widthPct * 100}%`,
        height: `${box.heightPct * 100}%`,
      }}
    >
      <div
        onMouseDown={startInteraction("move")}
        className="absolute inset-0 flex items-center justify-center cursor-grab rounded hover:border hover:border-dashed hover:border-white/50 active:cursor-grabbing"
        title="Drag to reposition"
      >
        <MdOpenWith
          className={`text-white drop-shadow ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          size={16}
        />
      </div>
      <div
        onMouseDown={startInteraction("resize")}
        className={`absolute -right-1 -bottom-1 w-3 h-3 rounded-sm bg-white border border-gray-500 cursor-se-resize ${
          isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
        title="Drag to resize"
      />
    </div>
  );
};

// Every generated image is normalized to the app's standard tile size for
// its source field, regardless of the base image's own native resolution.
export const TARGET_DIMENSIONS = {
  poster: { width: 710, height: 400 },
  portrait: { width: 600, height: 800 },
  background: { width: 1920, height: 1080 },
};

// One slot card — its own component (not just inline JSX in a `.map()`) so it
// can own a stable `containerRef` for the drag-to-reposition overlay and any
// other slot-local UI state, without those hooks colliding across slots.
export const HighlightSlotCard = ({
  slot,
  sourceMap,
  minioBaseUrl,
  onOverlaySelect,
  onAlignmentChange,
  onClearOverlay,
  onChange,
}) => {
  const previewRef = useRef(null);
  const hasLabel = Boolean(slot.labelText && slot.labelText.trim());
  const hasPreview = Boolean(slot.previewUrl);

  return (
    <div className="border border-gray-200 rounded-md p-2 bg-white">
      <div
        ref={previewRef}
        className="relative rounded overflow-hidden bg-gray-100"
        style={{ aspectRatio: slot.width && slot.height ? `${slot.width} / ${slot.height}` : "16 / 9" }}
      >
        {hasPreview ? (
          <img src={slot.previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400 text-center px-2">
            No image for this preview
          </div>
        )}
        {slot.generating && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-xs">
            Rendering…
          </div>
        )}
        {hasPreview && slot.overlayTid && slot.hasCutout === false && (
          <DraggableOverlay
            slot={slot}
            containerRef={previewRef}
            onCommit={(box) =>
              onChange({
                overlayXPct: box.xPct,
                overlayYPct: box.yPct,
                overlayWidthPct: box.widthPct,
                overlayHeightPct: box.heightPct,
              })
            }
          />
        )}
        {hasPreview && (
          <DraggableLabel
            slot={slot}
            containerRef={previewRef}
            onCommit={(xPct, yPct) => onChange({ labelXPct: xPct, labelYPct: yPct })}
          />
        )}
      </div>

      <div className="mt-2 space-y-1">
        {slot.overlayTid ? (
          <div className="flex items-center justify-between text-xs gap-1">
            <span className="truncate">
              Overlay: <strong>{slot.overlayTitle}</strong>
            </span>
            <button type="button" onClick={onClearOverlay} className="text-red-500 hover:text-red-700 shrink-0">
              Clear
            </button>
          </div>
        ) : (
          <TileSearchBox
            placeholder="Search tile to overlay…"
            onSelect={onOverlaySelect}
            sourceMap={sourceMap}
            minioBaseUrl={minioBaseUrl}
          />
        )}

        {!hasPreview && slot.overlayTid && (
          <p className="text-[10px] text-gray-400 mt-1">
            This slot has no base image for this preview — nothing to overlay onto here. Switch the
            Preview dropdown to see the other dimension.
          </p>
        )}

        {/* A designed black cutout in the background always wins — the
            overlay auto-fills it completely, so alignment here only picks
            which part of the overlay survives the crop (like CSS
            object-position). A plain background instead gets free
            drag/resize on the preview above, with no fixed slots at all. */}
        {hasPreview && slot.overlayTid && slot.hasCutout === true && (
          <div className="grid grid-cols-5 gap-1 mt-1">
            {ALIGNMENT_OPTIONS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => onAlignmentChange(a)}
                title={ALIGNMENT_LABELS[a]}
                className={`text-[10px] py-1 rounded border ${
                  slot.alignment === a
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {ALIGNMENT_LABELS[a]
                  .split(" ")
                  .map((w) => w[0])
                  .join("")}
              </button>
            ))}
          </div>
        )}

        {hasPreview && slot.overlayTid && slot.hasCutout === false && (
          <p className="text-[10px] text-gray-400 mt-1">
            Plain background — drag the overlay on the preview above to position and resize it.
          </p>
        )}

        {hasPreview && slot.overlayTid && slot.hasCutout == null && (
          <p className="text-[10px] text-gray-400 mt-1">Checking background…</p>
        )}

        {slot.overlayTid && (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
          <label className="block text-[10px] font-medium text-gray-500">Badge Text</label>
          <LabelTextInput value={slot.labelText} onCommit={(text) => onChange({ labelText: text })} />

          {hasLabel && (
            <div className="space-y-2 rounded border border-gray-100 bg-gray-50 p-2 mt-1">
              <p className="text-[10px] text-gray-400 -mt-0.5">
                Drag the badge on the preview above to position it, or set exactly:
              </p>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[10px] text-gray-500">
                  X%
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round((slot.labelXPct ?? 0.05) * 100)}
                    onChange={(e) =>
                      onChange({
                        labelXPct: Math.min(Math.max(Number(e.target.value) || 0, 0), 100) / 100,
                      })
                    }
                    className="w-14 text-xs py-0.5 px-1 border rounded border-gray-300"
                  />
                </label>
                <label className="flex items-center gap-1 text-[10px] text-gray-500">
                  Y%
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round((slot.labelYPct ?? 0.05) * 100)}
                    onChange={(e) =>
                      onChange({
                        labelYPct: Math.min(Math.max(Number(e.target.value) || 0, 0), 100) / 100,
                      })
                    }
                    className="w-14 text-xs py-0.5 px-1 border rounded border-gray-300"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[10px] text-gray-500">
                  Font
                  <select
                    value={slot.labelFontFamily}
                    onChange={(e) => onChange({ labelFontFamily: e.target.value })}
                    className="w-full text-xs py-1 px-1 border rounded border-gray-300 mt-0.5"
                  >
                    {LABEL_FONT_FAMILIES.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-[10px] text-gray-500">
                  Weight
                  <select
                    value={slot.labelFontWeight}
                    onChange={(e) => onChange({ labelFontWeight: e.target.value })}
                    className="w-full text-xs py-1 px-1 border rounded border-gray-300 mt-0.5"
                  >
                    {LABEL_FONT_WEIGHTS.map((w) => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 text-[10px] text-gray-500">
                  Size (px)
                  <input
                    type="number"
                    min={8}
                    placeholder="Auto"
                    value={slot.labelFontSize || ""}
                    onChange={(e) =>
                      onChange({ labelFontSize: e.target.value ? Number(e.target.value) : null })
                    }
                    className="w-16 text-xs py-0.5 px-1 border rounded border-gray-300"
                  />
                </label>
                <label className="flex items-center gap-1 text-[10px] text-gray-500">
                  <input
                    type="checkbox"
                    checked={slot.labelFontStyle === "italic"}
                    onChange={(e) => onChange({ labelFontStyle: e.target.checked ? "italic" : "normal" })}
                  />
                  Italic
                </label>
              </div>

              <label className="flex items-center gap-1 text-[10px] text-gray-500">
                <input
                  type="checkbox"
                  checked={slot.labelShowBackground}
                  onChange={(e) => onChange({ labelShowBackground: e.target.checked })}
                />
                Show background pill
              </label>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-gray-500">Text Color</span>
                  <ColorModeToggle
                    mode={slot.labelTextColorType}
                    onChange={(m) => onChange({ labelTextColorType: m })}
                  />
                </div>
                <ColorPicker
                  mode={slot.labelTextColorType}
                  solid={slot.labelTextColor}
                  from={slot.labelTextGradientFrom}
                  to={slot.labelTextGradientTo}
                  onSolidChange={(v) => onChange({ labelTextColor: v })}
                  onFromChange={(v) => onChange({ labelTextGradientFrom: v })}
                  onToChange={(v) => onChange({ labelTextGradientTo: v })}
                />
              </div>

              {slot.labelShowBackground && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-medium text-gray-500">Background Color</span>
                    <ColorModeToggle
                      mode={slot.labelBgColorType}
                      onChange={(m) => onChange({ labelBgColorType: m })}
                    />
                  </div>
                  <ColorPicker
                    mode={slot.labelBgColorType}
                    solid={slot.labelBgColor}
                    from={slot.labelBgGradientFrom}
                    to={slot.labelBgGradientTo}
                    onSolidChange={(v) => onChange({ labelBgColor: v })}
                    onFromChange={(v) => onChange({ labelBgGradientFrom: v })}
                    onToChange={(v) => onChange({ labelBgGradientTo: v })}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

const TileSearchBox = ({ placeholder, onSelect, sourceMap, minioBaseUrl }) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timeout = setTimeout(async () => {
      const { data, error } = await supabase
        .from("tiles")
        .select("*")
        .ilike("title", `%${term.trim()}%`)
        .limit(10);
      if (!cancelled) {
        setResults(error ? [] : data || []);
        setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [term]);

  return (
    <div className="relative">
      <input
        type="text"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full text-xs py-1.5 px-2 border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
      />
      {open && term.trim() && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded shadow-lg">
          {loading && <div className="p-2 text-xs text-gray-400">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-2 text-xs text-gray-400">No tiles found</div>
          )}
          {!loading &&
            results.map((tile) => {
              const thumb =
                safeJsonParseArray(tile.poster)[0] || safeJsonParseArray(tile.portrait)[0] || null;
              const sources = Array.isArray(tile.source) ? tile.source : [];
              const sourceLabel = sources.map((id) => sourceMap?.[id] || id).filter(Boolean).join(", ");
              return (
                <button
                  key={tile.tid}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(tile);
                    setTerm("");
                    setOpen(false);
                    setResults([]);
                  }}
                  className="flex items-center gap-2 w-full text-left px-2 py-1 hover:bg-gray-100"
                >
                  <span className="w-8 h-8 shrink-0 rounded overflow-hidden bg-gray-100">
                    {thumb && (
                      <img
                        src={resolveHighlightUrl(thumb, minioBaseUrl)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs truncate">{tile.title}</span>
                    {sourceLabel && (
                      <span className="block text-[10px] text-gray-400 truncate">{sourceLabel}</span>
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

// Each slot represents ONE (base tile, index) pair and always carries BOTH a
// Poster and a Portrait version, generated together: { key, id, baseTid,
//   baseImagePoster, baseImagePortrait, overlayTid, overlayTitle,
//   overlayTileData, alignment (shared),
//   posterOverlayXPct/YPct/WidthPct/HeightPct, portraitOverlay... (per-dimension
//   free-placement box, only used when that dimension's background has no
//   designed cutout), labelText, labelXPct, labelYPct, labelFontFamily,
//   labelFontSize, labelFontWeight, labelFontStyle, labelShowBackground,
//   labelTextColorType, labelTextColor, labelTextGradientFrom, labelTextGradientTo,
//   labelBgColorType, labelBgColor, labelBgGradientFrom, labelBgGradientTo (shared),
//   posterPreviewUrl/Width/Height/HasCutout/Blob/Generating,
//   portraitPreviewUrl/Width/Height/HasCutout/Blob/Generating, requestId }
//
// The "Preview" dropdown below only picks which of the two already-generated
// results each slot card displays/edits — both are always generated.
const DIMENSIONS = ["poster", "portrait", "background"];
const capitalize = (s) => s[0].toUpperCase() + s.slice(1);
const OVERLAY_BOX_KEYS = ["overlayXPct", "overlayYPct", "overlayWidthPct", "overlayHeightPct"];
// The badge is configured independently per dimension — a Poster badge's
// text/position/styling never leaks onto the Portrait or Background version.
export const LABEL_FIELD_KEYS = [
  "labelText",
  "labelXPct",
  "labelYPct",
  "labelFontFamily",
  "labelFontSize",
  "labelFontWeight",
  "labelFontStyle",
  "labelShowBackground",
  "labelTextColorType",
  "labelTextColor",
  "labelTextGradientFrom",
  "labelTextGradientTo",
  "labelBgColorType",
  "labelBgColor",
  "labelBgGradientFrom",
  "labelBgGradientTo",
];
const PER_DIMENSION_PATCH_KEYS = [...OVERLAY_BOX_KEYS, ...LABEL_FIELD_KEYS];
export const DEFAULT_LABEL_VALUES = {
  labelText: "",
  labelXPct: 0.05,
  labelYPct: 0.05,
  labelFontFamily: "Arial",
  labelFontSize: null,
  labelFontWeight: "700",
  labelFontStyle: "normal",
  labelShowBackground: true,
  labelTextColorType: "solid",
  labelTextColor: "#ffffff",
  labelTextGradientFrom: "#ffffff",
  labelTextGradientTo: "#cccccc",
  labelBgColorType: "gradient",
  labelBgColor: "#dc2626",
  labelBgGradientFrom: "#ec4899",
  labelBgGradientTo: "#dc2626",
};

const HighlightRowBuilder = ({
  baseTiles,
  setBaseTiles,
  slots,
  setSlots,
  minioBaseUrl,
  baseImageField,
  setBaseImageField,
}) => {
  const [sourceMap, setSourceMap] = useState({});
  // `key` (tid+image pair) identifies a slot's CONTENT, for preserving config
  // across re-pools. It is NOT guaranteed unique — the same image pair can
  // appear twice (duplicate uploads across tiles). `id` is a separate,
  // always-unique identity used for React keys and all state lookups, so
  // duplicate content never causes two slots to collide and silently stomp
  // each other's state.
  const idCounterRef = useRef(0);

  useEffect(() => {
    supabase
      .from("source")
      .select("id, source")
      .then(({ data, error }) => {
        if (error || !data) return;
        const map = {};
        data.forEach((s) => (map[s.id] = s.source));
        setSourceMap(map);
      });
  }, []);

  const addBaseTile = (tile) => {
    if (baseTiles.some((t) => t.tid === tile.tid)) return;
    setBaseTiles((prev) => [...prev, tile]);
  };

  const removeBaseTile = (tid) => {
    setBaseTiles((prev) => prev.filter((t) => t.tid !== tid));
  };

  // Runs the resize-to-target (+ optional overlay + optional label) generation
  // for BOTH dimensions (Poster and Portrait) of one slot, independently, and
  // applies each result as soon as it's ready. Used for brand-new slots (no
  // overlay yet — every output still needs to be resized to the standard
  // size) and for every subsequent overlay/alignment/position/label change.
  // Guarded by requestId so a slower, older request can never clobber a
  // newer one's result if several fire back-to-back. A dimension with no
  // base image at this slot's index (e.g. the base tile has fewer Portrait
  // images than Poster images) is simply skipped — that dimension has
  // nothing to generate.
  const runGeneration = (id, merged, opts = {}) => {
    DIMENSIONS.forEach((dimension) => {
      const baseImage = merged[`baseImage${capitalize(dimension)}`];
      if (!baseImage) return; // nothing to generate for this dimension at this slot

      const backgroundUrl = resolveHighlightUrl(baseImage, minioBaseUrl);
      const overlayPath = merged.overlayTid
        ? safeJsonParseArray(merged.overlayTileData?.[dimension])[0]
        : null;
      const overlayUrl = overlayPath ? resolveHighlightUrl(overlayPath, minioBaseUrl) : null;
      const target = TARGET_DIMENSIONS[dimension];

      generateCompositeImage({
        backgroundUrl,
        overlayUrl,
        alignment: merged.alignment,
        overlayXPct: merged[`${dimension}OverlayXPct`],
        overlayYPct: merged[`${dimension}OverlayYPct`],
        overlayWidthPct: merged[`${dimension}OverlayWidthPct`],
        overlayHeightPct: merged[`${dimension}OverlayHeightPct`],
        targetWidth: target.width,
        targetHeight: target.height,
        label: merged[`${dimension}LabelText`]
          ? {
              text: merged[`${dimension}LabelText`],
              xPct: merged[`${dimension}LabelXPct`],
              yPct: merged[`${dimension}LabelYPct`],
              fontFamily: merged[`${dimension}LabelFontFamily`],
              fontSize: merged[`${dimension}LabelFontSize`],
              fontWeight: merged[`${dimension}LabelFontWeight`],
              fontStyle: merged[`${dimension}LabelFontStyle`],
              showBackground: merged[`${dimension}LabelShowBackground`],
              textColorType: merged[`${dimension}LabelTextColorType`],
              textColor: merged[`${dimension}LabelTextColor`],
              textGradientFrom: merged[`${dimension}LabelTextGradientFrom`],
              textGradientTo: merged[`${dimension}LabelTextGradientTo`],
              bgColorType: merged[`${dimension}LabelBgColorType`],
              bgColor: merged[`${dimension}LabelBgColor`],
              bgGradientFrom: merged[`${dimension}LabelBgGradientFrom`],
              bgGradientTo: merged[`${dimension}LabelBgGradientTo`],
            }
          : null,
      })
        .then(({ blob, width, height, hasCutout }) => {
          const previewUrl = URL.createObjectURL(blob);
          setSlots((cur) =>
            cur.map((s) =>
              s.id === id && s.requestId === merged.requestId
                ? {
                    ...s,
                    [`${dimension}Blob`]: blob,
                    [`${dimension}PreviewUrl`]: previewUrl,
                    [`${dimension}Width`]: width,
                    [`${dimension}Height`]: height,
                    [`${dimension}HasCutout`]: hasCutout,
                    [`${dimension}Generating`]: false,
                    [`${dimension}Failed`]: false,
                  }
                : s
            )
          );
        })
        .catch((err) => {
          console.error(`Failed to generate ${dimension} highlight composite:`, err);
          // Mark this dimension Failed (not just "not generating") so the
          // edit-load catch-up effect below can tell "never attempted" apart
          // from "attempted and can't succeed" (e.g. an external image URL
          // blocked by CORS) — without this, that effect saw a blob-less,
          // non-generating dimension, assumed it just hadn't started yet, and
          // retriggered regeneration every render forever.
          setSlots((cur) =>
            cur.map((s) =>
              s.id === id && s.requestId === merged.requestId
                ? { ...s, [`${dimension}Generating`]: false, [`${dimension}Failed`]: true }
                : s
            )
          );
          if (!opts.silent) {
            Swal.fire({ icon: "error", title: "Couldn't generate preview", text: err.message });
          }
        });
    });
  };

  // Recompute the pooled slot list whenever base tiles change, preserving any
  // already-configured overlay/alignment/label for image pairs that are
  // still present. Each base tile contributes max(poster count, portrait
  // count) slots, pairing poster[i] with portrait[i] — if one field has
  // fewer images than the other, the shorter one's slots simply have no base
  // image for that dimension (that dimension is skipped for those slots).
  useEffect(() => {
    setSlots((prev) => {
      // Queue (not a single value) per content-key, so if the same image
      // pair legitimately appears more than once, each occurrence gets its
      // OWN previously-cached slot back instead of all collapsing onto one.
      const queues = new Map();
      prev.forEach((s) => {
        if (!queues.has(s.key)) queues.set(s.key, []);
        queues.get(s.key).push(s);
      });

      const freshSlots = [];

      const next = baseTiles.flatMap((tile) => {
        const imagesByDim = {
          poster: safeJsonParseArray(tile.poster),
          portrait: safeJsonParseArray(tile.portrait),
          background: safeJsonParseArray(tile.background),
        };
        const count = Math.max(...DIMENSIONS.map((d) => imagesByDim[d].length));

        return Array.from({ length: count }, (_, i) => {
          const imgByDim = {};
          DIMENSIONS.forEach((d) => {
            imgByDim[d] = imagesByDim[d][i] || null;
          });
          const key = buildSlotKey(tile.tid, DIMENSIONS.map((d) => imgByDim[d] || "_").join("|"));
          const queue = queues.get(key);
          const reused = queue && queue.length ? queue.shift() : null;
          if (reused) return reused;

          idCounterRef.current += 1;
          const fresh = {
            key,
            id: `${key}::${idCounterRef.current}`,
            baseTid: tile.tid,
            // This slot's position within its OWN base tile's pooled
            // sequence — saved on export so edit-reload can re-derive the
            // other two dimensions' base images by exact index instead of
            // re-counting (which would be wrong if a dimension has gaps,
            // e.g. fewer Background images than Poster/Portrait).
            tileIndex: i,
            overlayTid: null,
            overlayTitle: "",
            overlayTileData: null,
            alignment: "center",
            requestId: 1,
          };
          DIMENSIONS.forEach((d) => {
            const cap = capitalize(d);
            fresh[`baseImage${cap}`] = imgByDim[d];
            fresh[`${d}OverlayXPct`] = null;
            fresh[`${d}OverlayYPct`] = null;
            fresh[`${d}OverlayWidthPct`] = null;
            fresh[`${d}OverlayHeightPct`] = null;
            LABEL_FIELD_KEYS.forEach((k) => {
              fresh[`${d}${capitalize(k)}`] = DEFAULT_LABEL_VALUES[k];
            });
            fresh[`${d}PreviewUrl`] = imgByDim[d] ? resolveHighlightUrl(imgByDim[d], minioBaseUrl) : null;
            fresh[`${d}Width`] = null;
            fresh[`${d}Height`] = null;
            fresh[`${d}HasCutout`] = null;
            fresh[`${d}Blob`] = null;
            fresh[`${d}Generating`] = Boolean(imgByDim[d]);
          });
          freshSlots.push(fresh);
          return fresh;
        });
      });

      // Every brand-new slot needs to be resized to the standard target size
      // right away, even before any overlay is picked.
      freshSlots.forEach((s) => runGeneration(s.id, s));

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTiles]);

  // Clicking through overlay/field/alignment/label options fires several
  // composite generations back-to-back; they don't necessarily resolve in the
  // order they were started. Each slot gets a monotonically increasing
  // requestId so a slower, older request can never clobber a newer one's
  // result.
  const generatingFlagsFor = (slot) => {
    const flags = {};
    DIMENSIONS.forEach((d) => {
      flags[`${d}Generating`] = Boolean(slot[`baseImage${capitalize(d)}`]);
      // A fresh attempt (new overlay, alignment, label, etc.) should always
      // get to retry, even if a previous attempt for this slot failed.
      flags[`${d}Failed`] = false;
    });
    return flags;
  };

  const resetOverlayBoxFields = () => {
    const reset = {};
    DIMENSIONS.forEach((d) => {
      reset[`${d}OverlayXPct`] = null;
      reset[`${d}OverlayYPct`] = null;
      reset[`${d}OverlayWidthPct`] = null;
      reset[`${d}OverlayHeightPct`] = null;
    });
    return reset;
  };

  const regenerateSlot = (id, overrides, opts = {}) => {
    setSlots((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;

      const current = prev[idx];
      const requestId = (current.requestId || 0) + 1;
      const merged = {
        ...current,
        ...overrides,
        requestId,
        ...generatingFlagsFor(current),
      };

      runGeneration(id, merged, opts);

      const next = [...prev];
      next[idx] = merged;
      return next;
    });
  };

  // A slot loaded from an existing saved row only ever has ONE dimension's
  // real composite (a row only ever stores one dimension) — the other two
  // start with no blob and aren't marked as generating. If that slot has an
  // overlay, regenerate it here so all three dimensions show the real merged
  // image right away instead of a stale/plain placeholder. Self-limiting:
  // once regenerateSlot runs, every dimension gets marked generating (or a
  // blob, or Failed), so this won't refire for the same slot — the `Failed`
  // check matters because some base/overlay images are hosted externally
  // (e.g. a source CDN with no CORS headers) and can never succeed; without
  // it, a permanently failing dimension looked identical to "hasn't started
  // yet" and this effect regenerated it every render forever, each attempt
  // popping its own error dialog. Silent because this is an automatic
  // background catch-up, not a direct user action.
  useEffect(() => {
    slots.forEach((slot) => {
      if (!slot.overlayTid) return;
      const needsCatchUp = DIMENSIONS.some(
        (d) =>
          slot[`baseImage${capitalize(d)}`] &&
          !slot[`${d}Blob`] &&
          !slot[`${d}Generating`] &&
          !slot[`${d}Failed`]
      );
      if (needsCatchUp) {
        regenerateSlot(slot.id, {}, { silent: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const handleOverlaySelect = (id, tile) => {
    const fieldOptions = getOverlayImageFields(tile);
    if (fieldOptions.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Poster/Portrait/Background image",
        text: `"${tile.title}" has no image to overlay.`,
      });
      return;
    }
    regenerateSlot(id, {
      overlayTid: tile.tid,
      overlayTitle: tile.title,
      overlayTileData: tile,
      // Reset any custom position/size — it was tuned for the PREVIOUS
      // overlay's own aspect ratio/content, not this newly picked one.
      ...resetOverlayBoxFields(),
    });
  };

  const handleAlignmentChange = (id, alignment) => {
    regenerateSlot(id, { alignment });
  };

  const clearOverlay = (id) => {
    regenerateSlot(id, {
      overlayTid: null,
      overlayTitle: "",
      overlayTileData: null,
      ...resetOverlayBoxFields(),
    });
  };

  const remapDimensionPatch = (patch, dimension) => {
    const remapped = {};
    Object.entries(patch).forEach(([k, v]) => {
      remapped[PER_DIMENSION_PATCH_KEYS.includes(k) ? `${dimension}${capitalize(k)}` : k] = v;
    });
    return remapped;
  };

  return (
    <div className="space-y-4 col-span-1 sm:col-span-2 p-4">
      <div className="border border-gray-200 rounded-md p-3 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-1">Base Tiles</label>
        <p className="text-xs text-gray-400 mb-2">
          Search and add tiles — their Poster, Portrait, and Background images are all pooled into
          the slots below, and every slot's overlay gets merged onto all three automatically.
        </p>

        <label className="block text-xs font-medium text-gray-600 mb-1">Preview</label>
        <select
          value={baseImageField}
          onChange={(e) => setBaseImageField(e.target.value)}
          className="mb-2 text-xs py-1.5 px-2 border rounded border-gray-300 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="poster">Poster</option>
          <option value="portrait">Portrait</option>
          <option value="background">Background</option>
        </select>
        <p className="text-[10px] text-gray-400 -mt-1 mb-2">
          All three versions are always generated — this just picks which one the slots below show
          and let you edit.
        </p>

        <TileSearchBox
          placeholder="Search tiles by title…"
          onSelect={addBaseTile}
          sourceMap={sourceMap}
          minioBaseUrl={minioBaseUrl}
        />
        {baseTiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {baseTiles.map((t) => (
              <span
                key={t.tid}
                className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-300 rounded-full px-2 py-1"
              >
                {t.title}
                <button
                  type="button"
                  onClick={() => removeBaseTile(t.tid)}
                  className="text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {slots.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          Add at least one base tile to see its {baseImageField} images here.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {slots.map((slot) => {
            const dim = baseImageField;
            const viewSlot = {
              ...slot,
              previewUrl: slot[`${dim}PreviewUrl`],
              width: slot[`${dim}Width`],
              height: slot[`${dim}Height`],
              hasCutout: slot[`${dim}HasCutout`],
              compositeBlob: slot[`${dim}Blob`],
              generating: slot[`${dim}Generating`],
              overlayXPct: slot[`${dim}OverlayXPct`],
              overlayYPct: slot[`${dim}OverlayYPct`],
              overlayWidthPct: slot[`${dim}OverlayWidthPct`],
              overlayHeightPct: slot[`${dim}OverlayHeightPct`],
            };
            LABEL_FIELD_KEYS.forEach((k) => {
              viewSlot[k] = slot[`${dim}${capitalize(k)}`];
            });
            return (
              <HighlightSlotCard
                key={slot.id}
                slot={viewSlot}
                sourceMap={sourceMap}
                minioBaseUrl={minioBaseUrl}
                onOverlaySelect={(tile) => handleOverlaySelect(slot.id, tile)}
                onAlignmentChange={(alignment) => handleAlignmentChange(slot.id, alignment)}
                onClearOverlay={() => clearOverlay(slot.id)}
                onChange={(patch) => regenerateSlot(slot.id, remapDimensionPatch(patch, dim))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HighlightRowBuilder;
